import React, { useState } from "react";

import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Divider,
  LinearProgress,
} from "@material-ui/core";

import {
  makeStyles,
} from "@material-ui/core/styles";

import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import BuildIcon from "@material-ui/icons/Build";
import AssessmentIcon from "@material-ui/icons/Assessment";
import SettingsIcon from "@material-ui/icons/Settings";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row-reverse",
    direction: "rtl",
    minHeight: "100vh",
    background: "#eeeeee",
    fontFamily: "Tahoma, Arial, sans-serif",
    overflow: "hidden",
  },

  sidebar: {
    width: 215,
    minWidth: 215,
    background: "#383838",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
  },

  sidebarTitle: {
    padding: "25px 12px",
    fontWeight: "bold",
    background: "#858585",
    textAlign: "center",
  },

  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "20px 15px",
    cursor: "pointer",
    fontSize: 15,
    "&:hover": {
      background: "#505050",
    },
  },

  content: {
    flex: 1,
    minWidth: 0,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  topPanel: {
    padding: 10,
    border: "1px solid #bcbcbc",
    background: "#fafafa",
  },

  section: {
    padding: 8,
    border: "1px solid #bcbcbc",
    background: "#fafafa",
    borderRadius: 2,
  },

  field: {
    background: "#fff",
    "& input": {
      textAlign: "center",
      padding: "7px 8px",
      fontSize: 16,
    },
  },

  label: {
    fontWeight: "bold",
    fontSize: 14,
    whiteSpace: "nowrap",
  },

  yearField: {
    background: "#fff",
    "& input": {
      textAlign: "center",
      fontSize: 21,
      fontWeight: "bold",
      padding: "7px 4px",
      color: "#193cff",
    },
  },

  greenText: {
    color: "#159447",
    fontWeight: "bold",
  },

  redText: {
    color: "#ff2424",
    fontWeight: "bold",
  },

  accountButton: {
    width: "100%",
    height: 35,
    borderRadius: 0,
    border: "1px solid #d0d0d0",
    background: "#f1f1f1",
    fontWeight: "bold",
    fontSize: 14,
  },

  mainButton: {
    height: 62,
    width: "100%",
    fontSize: 24,
    fontWeight: "bold",
    border: "1px solid #999",
    borderRadius: 0,
    background: "#f5f5f5",
    color: "#111",
    "&:hover": {
      background: "#e7efff",
    },
  },

  secondaryButton: {
    height: 45,
    width: "100%",
    borderRadius: 0,
    border: "1px solid #aaa",
    background: "#f4f4f4",
    fontSize: 17,
    fontWeight: "bold",
  },

  status: {
    minHeight: 42,
    padding: 8,
    textAlign: "center",
    color: "#164dff",
    background: "#fff",
    border: "1px solid #aaa",
    fontWeight: "bold",
    fontSize: 15,
  },

  checklist: {
    padding: "5px 0",
    fontSize: 13,
    fontWeight: "bold",
    "& .MuiCheckbox-root": {
      padding: 4,
    },
  },

  select: {
    width: "100%",
    background: "#fff",
    "& .MuiSelect-select": {
      padding: "7px 10px",
      fontWeight: "bold",
    },
  },

  dateInput: {
    background: "#fff",
    "& input": {
      padding: "7px 5px",
      fontSize: 14,
      textAlign: "center",
    },
  },

  "@media (max-width: 900px)": {
    sidebar: {
      display: "none",
    },

    content: {
      padding: 4,
    },

    label: {
      fontSize: 12,
    },

    mainButton: {
      fontSize: 19,
    },
  },
}));

const initialSteps = [
  { id: 1, title: "إنشاء ملف السكربت", checked: false },
  { id: 2, title: "إنشاء الجداول", checked: false },
  { id: 3, title: "إنشاء القيود", checked: false },
  { id: 4, title: "إنشاء الدوال", checked: false },
  { id: 5, title: "إنشاء التسلسلات", checked: false },
  { id: 6, title: "إنشاء الإجراءات المخزنة", checked: false },
  { id: 7, title: "إنشاء قاعدة بيانات السكربت", checked: false },
  { id: 8, title: "نقل الحسابات", checked: false },
  { id: 9, title: "نقل المخزون", checked: false },
];

export default function AnnualClosing() {
  const classes = useStyles();

  const [currentYear, setCurrentYear] = useState("2025");
  const [newYear, setNewYear] = useState("2026");

  const [currentDatabase, setCurrentDatabase] = useState(
    "OraxLite_NasmatAlsahraa"
  );

  const [newDatabase, setNewDatabase] = useState("OraxLite2026");

  const [openingStockAccount, setOpeningStockAccount] =
    useState("1270001");

  const [closingStockAccount, setClosingStockAccount] =
    useState("1270002");

  const [profitLossAccount, setProfitLossAccount] =
    useState("2320001");

  const [fromDate, setFromDate] = useState("2026-01-01");
  const [toDate, setToDate] = useState("2027-12-31");

  const [useDate, setUseDate] = useState(false);

  const [itemFilter, setItemFilter] = useState("1");

  const [valuationMethod, setValuationMethod] = useState("1");

  const [transferStock, setTransferStock] = useState(true);
  const [transferAccounts, setTransferAccounts] = useState(true);
  const [transferSubAccounts, setTransferSubAccounts] = useState(true);
  const [costCenters, setCostCenters] = useState(false);

  const [steps, setSteps] = useState(initialSteps);

  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState("");

  const handleStepChange = (id) => {
    setSteps((previous) =>
      previous.map((step) =>
        step.id === id
          ? { ...step, checked: !step.checked }
          : step
      )
    );
  };

  const startClosing = async () => {
    if (!currentDatabase.trim() || !newDatabase.trim()) {
      setStatus("يرجى إدخال قاعدة البيانات الحالية والجديدة");
      return;
    }

    if (currentDatabase.trim() === newDatabase.trim()) {
      setStatus("يجب أن يكون اسم قاعدة البيانات الجديدة مختلفاً");
      return;
    }

    if (Number(newYear) !== Number(currentYear) + 1) {
      setStatus("يجب أن تكون السنة الجديدة تالية للسنة الحالية");
      return;
    }

    if (fromDate > toDate) {
      setStatus("تاريخ البداية يجب أن يكون قبل تاريخ النهاية");
      return;
    }

    setRunning(true);
    setStatus("جاري تنفيذ الإقفال، الرجاء الانتظار ...");

    try {
      /*
       * اربط هذه الدالة مع API الإقفال السنوي لديك.
       *
       * مثال:
       *
       * await axios.post("/api/AnnualClosing/Start", {
       *   currentYear,
       *   newYear,
       *   currentDatabase,
       *   newDatabase,
       *   openingStockAccount,
       *   closingStockAccount,
       *   profitLossAccount,
       *   fromDate,
       *   toDate,
       *   useDate,
       *   itemFilter,
       *   valuationMethod,
       *   transferStock,
       *   transferAccounts,
       *   transferSubAccounts,
       *   costCenters,
       * });
       */

      setStatus(
        "تم تجهيز بيانات الإقفال. اربط الزر مع API لتنفيذ الإقفال الفعلي."
      );
    } catch (error) {
      setStatus(
        error?.response?.data?.message ||
          "حدث خطأ أثناء تنفيذ الإقفال السنوي"
      );
    } finally {
      setRunning(false);
    }
  };

  const renderAccountField = (
    label,
    value,
    onChange
  ) => (
    <Grid
      container
      alignItems="center"
      spacing={1}
      style={{ marginBottom: 5 }}
    >
      <Grid item xs={4}>
        <Typography className={classes.label}>
          {label}
        </Typography>
      </Grid>

      <Grid item xs={3}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </Grid>

      <Grid item xs={5}>
        <Button className={classes.accountButton}>
          {label === "حساب مخزون أول المدة"
            ? "مخزون أول المدة"
            : label === "حساب مخزون آخر المدة"
            ? "مخزون آخر المدة"
            : "أرباح وخسائر الفترة"}
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <div className={classes.root} style={{direction:"ltr"}}>
      {/* القائمة الجانبية */}

      <aside className={classes.sidebar}>
        <Typography className={classes.sidebarTitle}>
          الإقفال السنوي
        </Typography>

        <div className={classes.menuItem}>
          <AccountBalanceIcon />
          الإقفال السنوي
        </div>

        <div className={classes.menuItem}>
          <BuildIcon />
          أدوات أخرى
        </div>

        <div className={classes.menuItem}>
          <AssessmentIcon />
          التقارير
        </div>

        <div className={classes.menuItem}>
          <SettingsIcon />
          الإعدادات
        </div>
      </aside>

      {/* محتوى الواجهة */}

      <main className={classes.content}>
        {/* بيانات السنوات وقواعد البيانات */}

        <Paper className={classes.topPanel} elevation={0}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={3} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.yearField}
                value={currentYear}
                onChange={(e) => setCurrentYear(e.target.value)}
              />
            </Grid>

            <Grid item xs={3} md={2}>
              <Typography className={classes.label}>
                السنة الحالية
              </Typography>
            </Grid>

            <Grid item xs={6} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.field}
                value={currentDatabase}
                onChange={(e) => setCurrentDatabase(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography className={classes.label}>
                القاعدة الحالية
              </Typography>
            </Grid>

            <Grid item xs={3} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.yearField}
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                InputProps={{
                  style: { color: "red" },
                }}
              />
            </Grid>

            <Grid item xs={3} md={2}>
              <Typography className={classes.label}>
                السنة الجديدة
              </Typography>
            </Grid>

            <Grid item xs={6} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.field}
                value={newDatabase}
                onChange={(e) => setNewDatabase(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography className={classes.label}>
                القاعدة الجديدة
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* القائمة الرئيسية */}

        <Grid container spacing={1}>
          {/* خطوات الإقفال */}

          <Grid item xs={12} md={4}>
            <Paper
              className={classes.section}
              elevation={0}
              style={{ minHeight: 420 }}
            >
              {steps.map((step) => (
                <Box
                  key={step.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  minHeight={43}
                  className={classes.greenText}
                >
                  <Typography
                    style={{
                      fontSize: 13,
                      fontWeight: "bold",
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Checkbox
                    color="primary"
                    size="small"
                    checked={step.checked}
                    onChange={() => handleStepChange(step.id)}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* الحسابات والفترة المحاسبية */}

          <Grid item xs={12} md={8}>
            <Paper
              className={classes.section}
              elevation={0}
            >
              {renderAccountField(
                "حساب مخزون أول المدة",
                openingStockAccount,
                setOpeningStockAccount
              )}

              {renderAccountField(
                "حساب مخزون آخر المدة",
                closingStockAccount,
                setClosingStockAccount
              )}

              {renderAccountField(
                "حساب الأرباح والخسائر",
                profitLossAccount,
                setProfitLossAccount
              )}
            </Paper>

            <Paper
              className={classes.section}
              elevation={0}
              style={{ marginTop: 6 }}
            >
              <Typography
                className={classes.label}
                style={{ marginBottom: 8 }}
              >
                الفترة المحاسبية
              </Typography>

              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    type="date"
                    variant="outlined"
                    size="small"
                    className={classes.dateInput}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Typography align="center">
                    إلى
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    type="date"
                    variant="outlined"
                    size="small"
                    className={classes.dateInput}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useDate}
                        onChange={(e) =>
                          setUseDate(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="استخدام التاريخ"
                  />
                </Grid>

                <Grid item xs={4}>
                  <Typography className={classes.label}>
                    فلترة الأصناف حسب
                  </Typography>
                </Grid>

                <Grid item xs={8}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.select}
                  >
                    <Select
                      value={itemFilter}
                      onChange={(e) =>
                        setItemFilter(e.target.value)
                      }
                    >
                      <MenuItem value="1">
                        الأصناف ما عدا الصفرية
                      </MenuItem>

                      <MenuItem value="2">
                        جميع الأصناف
                      </MenuItem>

                      <MenuItem value="3">
                        الأصناف ذات الرصيد فقط
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={4}>
                  <Typography className={classes.label}>
                    طريقة التقييم
                  </Typography>
                </Grid>

                <Grid item xs={8}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.select}
                  >
                    <Select
                      value={valuationMethod}
                      onChange={(e) =>
                        setValuationMethod(e.target.value)
                      }
                    >
                      <MenuItem value="1">
                        متوسط سعر التكلفة
                      </MenuItem>

                      <MenuItem value="2">
                        آخر سعر شراء
                      </MenuItem>

                      <MenuItem value="3">
                        سعر التكلفة المحدد
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* زر بدء الإقفال */}

            <Box mt={1}>
              <Button
                className={classes.mainButton}
                onClick={startClosing}
                disabled={running}
              >
                {running
                  ? "جاري تنفيذ الإقفال..."
                  : "ابدأ عملية الإقفال السنوي"}
              </Button>
            </Box>

            {/* الدليل والحسابات الوسيطة */}

            <Grid container spacing={1} style={{ marginTop: 1 }}>
              <Grid item xs={6}>
                <Button
                  className={classes.secondaryButton}
                  onClick={() =>
                    alert("اربط هذا الزر بشاشة الحسابات الوسيطة")
                  }
                >
                  الحسابات الوسيطة
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  className={classes.secondaryButton}
                  onClick={() =>
                    alert("اربط هذا الزر بشاشة الدليل المحاسبي")
                  }
                >
                  الدليل المحاسبي
                </Button>
              </Grid>
            </Grid>

            {/* خيارات الترحيل */}

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControlLabel
                  className={classes.redText}
                  control={
                    <Checkbox
                      checked={transferAccounts}
                      onChange={(e) =>
                        setTransferAccounts(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="نقل أرصدة الحسابات"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  className={classes.redText}
                  control={
                    <Checkbox
                      checked={transferStock}
                      onChange={(e) =>
                        setTransferStock(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="نقل أرصدة المخزون"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  className={classes.redText}
                  control={
                    <Checkbox
                      checked={costCenters}
                      onChange={(e) =>
                        setCostCenters(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="الإقفال حسب مراكز التكلفة"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={transferSubAccounts}
                      onChange={(e) =>
                        setTransferSubAccounts(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="نقل الحسابات الفرعية"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* حالة العملية */}

        <Paper className={classes.status} elevation={0}>
          {running && (
            <LinearProgress style={{ marginBottom: 8 }} />
          )}

          {status || "جاهز لتنفيذ الإقفال السنوي"}
        </Paper>
      </main>
    </div>
  );
}